/* Squigli birthday return gifts: theme picker, live header-card preview,
   guest count (slider + typeable number, tappable bulk-price pills), AJAX add to the cart drawer.
   Customer-facing words come from the section's [data-i18n] JSON (English built in as a fallback); the header card
   and everything that goes into the order (Theme, Birthday child, Age, Party date) stay in English. */
(() => {
  if (customElements.get('return-gifts')) return;

  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };
  const isoDate = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const DATE_OPTS = {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'};
  // prettyDate goes into the order (always English); uiDate is shown to the customer in the page's language
  const prettyDate = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', DATE_OPTS);
  const uiDate = (iso) => {
    const lang = document.documentElement.lang || 'en';
    if (lang.startsWith('en')) return prettyDate(iso);
    try { return new Date(iso + 'T12:00:00').toLocaleDateString(`${lang === 'ar' ? 'ar-LB' : lang}-u-nu-latn`, DATE_OPTS); } catch (e) { return prettyDate(iso); }
  };

  class ReturnGifts extends HTMLElement {
    connectedCallback() {
      this.min = Number(this.dataset.min) || 1;
      this.price = Number(this.dataset.price) || 0;
      this.cur = this.dataset.currency || '$';
      this.leadDays = Number(this.dataset.leadDays) || 0;
      try { this.i18n = JSON.parse((this.querySelector('[data-i18n]') || {}).textContent || '{}'); } catch (e) { this.i18n = {}; }
      // "20:4.5, 30:4" -> [{from:20, price:4.5}, ...] highest first
      this.tiers = String(this.dataset.tiers || '').split(',').map((t) => t.split(':').map((x) => Number(String(x).trim())))
        .filter(([from, price]) => from > 0 && price > 0).map(([from, price]) => ({from, price})).sort((a, b) => b.from - a.from);

      const $ = (s) => this.querySelector(s);
      this.els = {
        themes: [...this.querySelectorAll('[data-theme]')], card: $('[data-card]'), name: $('[data-card-name]'), cardBg: $('[data-card-bg]'),
        artL: $('[data-art-l]'), artR: $('[data-art-r]'), photo: $('[data-photo]'), soon: $('[data-soon]'), soonArt: $('[data-soon-art]'),
        child: $('[data-child]'), age: $('[data-age]'), date: $('[data-date]'), dateHelp: $('[data-date-help]'),
        qty: $('[data-qty]'), slider: $('[data-slider]'), ticks: $('[data-ticks]'), each: $('[data-each]'), total: $('[data-total]'), tierList: $('[data-tier-list]'),
        form: $('[data-form]'), submit: $('[data-submit]'), msg: $('[data-msg]'),
        customFields: $('[data-custom-fields]'), customDesc: $('[data-custom-desc]'),
        quoteArea: $('[data-quote-area]'), quote: $('[data-quote]'), priced: [...this.querySelectorAll('[data-priced]')],
      };
      this.whatsapp = String(this.dataset.whatsapp || '').replace(/\D/g, '');

      // price pills: "$4.50" not "$4.5"; tapping one jumps the guest count to that tier
      const ascending = [...this.tiers].reverse();
      this.els.tierList.querySelectorAll('[data-tier]').forEach((pill) => {
        const i = Number(pill.dataset.tier);
        const t = i === 0 ? {from: this.min, price: this.price} : ascending[i - 1];
        if (t) { pill.textContent = this.t('tier', '%from%+ guests: %price% each', {from: t.from, price: this.money(t.price)}); pill.dataset.from = t.from; }
        pill.addEventListener('click', () => this.setGuests(Number(pill.dataset.from)));
      });

      // slider: marks where the price drops
      this.sliderMax = this.els.slider ? Number(this.els.slider.max) || 50 : 0;
      if (this.els.ticks) {
        ascending.filter((t) => t.from > this.min && t.from < this.sliderMax).forEach((t) => {
          const tick = document.createElement('span');
          tick.className = 'rg__tick';
          tick.dataset.from = t.from;
          tick.style.setProperty('--at', this.sliderFrac(t.from));
          this.els.ticks.append(tick);
        });
      }

      const earliest = new Date(); earliest.setDate(earliest.getDate() + this.leadDays);
      this.earliest = isoDate(earliest);
      this.els.date.min = this.earliest;
      this.els.dateHelp.textContent = this.t('date_help', 'We need at least %days% days, so the earliest party date is %date%.', {days: this.leadDays, date: uiDate(this.earliest)});

      this.els.themes.forEach((b) => b.addEventListener('click', () => { const was = this.theme; this.pick(b); if (b !== was) this.effect(b); }));
      this.addEventListener('keydown', (e) => this.arrowKeys(e));
      [this.els.child, this.els.age, this.els.date, this.els.customDesc].filter(Boolean).forEach((el) => el.addEventListener('input', () => this.update()));
      if (this.els.quote) this.els.quote.addEventListener('click', () => this.requestQuote());
      this.els.qty.addEventListener('input', () => this.update());
      if (this.els.slider) this.els.slider.addEventListener('input', () => { this.els.qty.value = this.els.slider.value; this.update(); });
      this.els.qty.addEventListener('change', () => { this.els.qty.value = this.guests(); this.update(); });
      this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => {
        this.setGuests(this.guests() + Number(b.dataset.step));
      }));
      if (this.els.form) this.els.form.addEventListener('submit', (e) => this.add(e));

      // ?theme=halloween (or any theme name) opens the builder with that theme already picked
      const want = new URLSearchParams(location.search).get('theme');
      const byParam = want && this.els.themes.find((b) => {
        const n = (b.querySelector('.rg__theme-name')?.textContent || '').toLowerCase(), v = (b.dataset.name || '').toLowerCase();
        const w = want.toLowerCase().replace(/[-_]/g, ' ');
        return n === w || v.startsWith(w) || n.replace(/\s+/g, '') === w.replace(/\s+/g, '');
      });
      this.pick(byParam || this.els.themes.find((b) => b.getAttribute('aria-checked') === 'true') || this.els.themes[0]);
      if (document.fonts) document.fonts.ready.then(() => this.fitName());
      // Until a name is typed, the card cycles through the theme's example names.
      this.startExamples();
      new ResizeObserver(() => this.fitName()).observe(this.els.card);
    }

    // A customer-facing string in the page's language: t('tier', '%from%+ guests', {from: 20})
    t(key, fallback, vars) {
      let s = this.i18n[key] || fallback;
      if (this.i18n[key]) {             // Shopify's t filter HTML-escapes ("I'd" arrives as "I&#39;d"); these end up as plain text
        const ta = document.createElement('textarea');
        ta.innerHTML = s;
        s = ta.value;
      }
      Object.keys(vars || {}).forEach((k) => { s = s.split(`%${k}%`).join(String(vars[k])); });
      return s;
    }

    // (Re)start the example-name rotation, so a newly picked theme shows its first name for a full beat.
    startExamples() {
      clearInterval(this.exampleTimer);
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.exampleTimer = setInterval(() => {
        if (document.hidden || this.els.child.value.trim() || this.examples.length < 2) return;
        this.exampleAt = (this.exampleAt + 1) % this.examples.length;
        this.els.name.classList.add('is-swapping');
        setTimeout(() => { this.update(); this.els.name.classList.remove('is-swapping'); }, 220);
      }, 2800);
    }

    // Seasonal fun: picking a theme with an effect (Halloween) sends a swarm of bats across the whole page.
    effect(btn) {
      if (btn.dataset.effect !== 'bats' || this.swarming) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const src = btn.dataset.effectImg;
      if (!src) return;
      this.swarming = true;
      const layer = document.createElement('div');
      layer.className = 'rg-bats';
      layer.setAttribute('aria-hidden', 'true');
      document.body.append(layer);
      layer.animate([{opacity: 0}, {opacity: 1, offset: 0.15}, {opacity: 1, offset: 0.75}, {opacity: 0}], {duration: 4200, easing: 'ease-in-out', fill: 'forwards'});
      const vw = window.innerWidth, vh = window.innerHeight, count = vw < 600 ? 10 : 15;
      for (let i = 0; i < count; i++) {
        const size = (vw < 600 ? 56 : 70) + Math.random() * (vw < 600 ? 70 : 120);
        const ltr = Math.random() < 0.5;
        const y0 = Math.random() * vh * 0.85, y1 = Math.random() * vh * 0.85;
        const bat = document.createElement('span');
        bat.className = 'rg-bat';
        bat.style.width = `${size}px`;
        const img = document.createElement('img');
        img.src = src; img.alt = ''; img.width = 595; img.height = 280;
        bat.append(img); layer.append(bat);
        const x0 = ltr ? -size - 40 : vw + 40, x1 = ltr ? vw + 40 : -size - 40, flip = ltr ? -1 : 1;   // the artwork faces left
        const wob = (Math.random() - 0.5) * vh * 0.25;
        bat.animate([
          {transform: `translate(${x0}px, ${y0}px) scaleX(${flip}) rotate(${-8 * flip}deg)`},
          {transform: `translate(${(x0 + x1) / 2}px, ${(y0 + y1) / 2 + wob}px) scaleX(${flip}) rotate(${6 * flip}deg)`},
          {transform: `translate(${x1}px, ${y1}px) scaleX(${flip}) rotate(${-8 * flip}deg)`},
        ], {duration: 2300 + Math.random() * 1400, delay: Math.random() * 900, easing: 'ease-in-out', fill: 'both'});
        img.animate([{transform: 'scaleY(1)'}, {transform: 'scaleY(0.45)'}, {transform: 'scaleY(1)'}],
          {duration: 180 + Math.random() * 80, iterations: Infinity});
      }
      setTimeout(() => { layer.remove(); this.swarming = false; }, 4400);
    }

    setGuests(n) {
      this.els.qty.value = Math.min(150, Math.max(this.min, n));
      this.update();
    }

    // how far along the slider n sits, 0..1 (CSS offsets it by the thumb's width)
    sliderFrac(n) {
      const span = this.sliderMax - this.min;
      return span > 0 ? (Math.min(Math.max(n, this.min), this.sliderMax) - this.min) / span : 0;
    }

    guests() {
      const n = Math.floor(Number(this.els.qty.value));
      return Number.isFinite(n) && n >= this.min ? Math.min(n, 150) : this.min;
    }

    priceFor(n) {
      const t = this.tiers.find((x) => n >= x.from);
      return t ? t.price : this.price;
    }

    money(n) {
      return this.cur + (Number.isInteger(n) ? String(n) : n.toFixed(2));
    }

    arrowKeys(e) {
      if (!e.target.matches('[data-theme]') || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const i = this.els.themes.indexOf(e.target), d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
      const next = this.els.themes[(i + d + this.els.themes.length) % this.els.themes.length];
      this.pick(next); next.focus();
    }

    pick(btn) {
      if (!btn) return;
      this.theme = btn;
      this.custom = btn.hasAttribute('data-custom');
      // "Maria 6, Yara 5" -> [{name:'Maria', age:6}, ...]
      this.examples = String(btn.dataset.examples || '').split(',').map((e) => {
        const m = e.trim().match(/^(.*?)\s+(\d{1,2})$/);
        return m ? {name: m[1].trim(), age: Number(m[2])} : (e.trim() ? {name: e.trim(), age: 6} : null);
      }).filter(Boolean);
      this.exampleAt = 0;
      const first = this.examples[0];
      this.els.child.placeholder = this.t('example', 'e.g. %name%', {name: first ? first.name : 'Maria'});
      this.els.age.placeholder = first ? String(first.age) : '6';
      if (this.els.name) this.startExamples();
      if (this.els.customFields) this.els.customFields.hidden = !this.custom;
      if (this.els.quoteArea) this.els.quoteArea.hidden = !this.custom;
      this.els.priced.forEach((el) => { el.hidden = this.custom; });
      this.els.themes.forEach((b) => {
        const on = b === btn;
        b.setAttribute('aria-checked', String(on));
        b.tabIndex = on ? 0 : -1;
      });
      // card colours come from the chosen theme's CSS variables
      ['--rg-card', '--rg-card-2', '--rg-ink', '--rg-accent'].forEach((v) => this.els.card.style.setProperty(v, btn.style.getPropertyValue(v)));
      // designed card: artwork image + the name line drawn on top in the card's font
      const designed = Boolean(btn.dataset.cardImage);
      this.els.card.classList.toggle('rg__card--designed', designed);
      this.els.cardBg.hidden = !designed;
      if (designed) {
        this.els.cardBg.src = btn.dataset.cardImage;
        this.els.card.style.setProperty('--name-top', btn.dataset.nameTop + '%');
        this.els.card.style.setProperty('--name-size', btn.dataset.nameSize + 'cqw');
        this.els.card.style.setProperty('--name-color', btn.dataset.nameColor);
        this.nameMax = Number(btn.dataset.nameMax) || 70;
      }
      const art = btn.dataset.art;
      this.els.artL.style.backgroundImage = art ? `url("${art}")` : 'none';
      this.els.artR.textContent = btn.dataset.artR || '';
      const photo = btn.dataset.photo;
      this.els.photo.hidden = !photo;
      this.els.soon.hidden = Boolean(photo);
      if (photo) { this.els.photo.src = photo; this.els.photo.alt = this.t('photo_alt', '%theme% return gift bag', {theme: btn.querySelector('.rg__theme-name').textContent}); }
      this.els.soonArt.style.backgroundImage = art ? `url("${art}")` : 'none';
      const soonText = this.querySelector('[data-soon-text]');
      if (soonText) soonText.textContent = this.custom ? this.t('soon_custom', 'Your theme, designed for your party ✨') : this.t('soon', 'Photo coming soon');
      this.update();
    }

    update() {
      const name = this.els.child.value.trim().toUpperCase();
      const age = Math.floor(Number(this.els.age.value));
      const designed = this.els.card.classList.contains('rg__card--designed');
      const apos = designed ? '\u2019' : "'";
      const example = this.examples && this.examples[this.exampleAt];
      const shown = name || (example ? example.name.toUpperCase() : `YOUR CHILD`);
      const who = `${shown}${apos}S`;
      const n = age >= 1 && age <= 18 ? age : (example ? example.age : 1);
      this.els.name.innerHTML = '';
      this.els.name.append(`${who} `);
      const ageEl = document.createElement('span');
      ageEl.append(String(n));
      const sup = document.createElement('sup'); sup.textContent = ordinal(n); ageEl.append(sup);
      this.els.name.append(ageEl, ' Birthday');
      this.els.card.classList.toggle('is-placeholder', !name);
      this.fitName();

      const guests = this.guests();
      const each = this.priceFor(guests);
      this.els.each.textContent = this.t('each_line', '%count% bags × %price%', {count: guests, price: this.money(each)});
      this.els.total.textContent = this.money(Math.round(guests * each * 100) / 100);
      const active = this.tiers.find((x) => guests >= x.from);
      const activeIdx = active ? this.tiers.length - this.tiers.indexOf(active) : 0;
      this.els.tierList.querySelectorAll('[data-tier]').forEach((pill) => {
        const on = Number(pill.dataset.tier) === activeIdx;
        pill.classList.toggle('is-on', on);
        pill.setAttribute('aria-pressed', String(on));
      });
      if (this.els.slider) {
        this.els.slider.value = Math.min(guests, this.sliderMax);
        this.els.slider.setAttribute('aria-valuetext', this.t('guests_value', '%count% guests', {count: guests}));
        this.els.slider.parentElement.style.setProperty('--p', this.sliderFrac(guests));
        this.els.ticks.querySelectorAll('.rg__tick').forEach((t) => {
          t.classList.toggle('is-passed', guests >= Number(t.dataset.from));
          t.classList.toggle('is-at', guests === Number(t.dataset.from));   // hidden under the thumb
        });
      }

      const problem = this.problem();
      const button = this.custom ? this.els.quote : this.els.submit;
      if (button) button.disabled = Boolean(problem);
      this.els.msg.textContent = problem && problem !== 'incomplete' ? problem : '';
    }

    quoteText() {
      const age = Math.floor(Number(this.els.age.value));
      return [
        this.t('quote_hi', "Hi Squigli! 👋 I'd like a quote for birthday return gifts with my own theme."),
        '',
        this.t('quote_theme', '🎨 Theme: %value%', {value: this.els.customDesc.value.trim()}),
        this.t('quote_for', '🎂 For: %name%, turning %age%', {name: this.els.child.value.trim(), age}),
        this.t('quote_date', '📅 Party date: %date%', {date: uiDate(this.els.date.value)}),
        this.t('quote_guests', '👧 Guests: %count%', {count: this.guests()}),
        '',
        this.t('quote_photos', "I'll send inspiration photos here if I have any."),
      ].join('\n');
    }

    requestQuote() {
      if (this.problem() || !this.whatsapp) return;
      window.open(`https://wa.me/${this.whatsapp}?text=${encodeURIComponent(this.quoteText())}`, '_blank', 'noopener');
    }

    // Keep long names inside the card's clear area by scaling the line down.
    fitName() {
      const el = this.els.name;
      el.style.transform = '';
      if (!this.els.card.classList.contains('rg__card--designed')) return;
      const max = this.els.card.clientWidth * (this.nameMax || 70) / 100;
      const w = el.scrollWidth;
      if (w > max && w > 0) el.style.transform = `translateX(-50%) scale(${max / w})`;
    }

    problem() {
      if (this.custom) {
        if (!this.whatsapp) return this.t('err_quote_setup', 'Quote requests aren’t set up yet.');
        if (!this.els.customDesc.value.trim()) return 'incomplete';
      } else if (!this.theme.dataset.variant) return this.t('err_variant', 'This theme isn’t set up on the product yet.');
      if (this.theme.dataset.available === 'false') return this.t('err_soldout', 'This theme is sold out right now.');
      // the card is printed in English letters (accents are fine)
      if (Array.from(this.els.child.value).some((ch) => /\p{L}/u.test(ch) && !/[A-Za-z\u00C0-\u024F]/.test(ch))) {
        return this.t('err_latin', 'Please write the name in English letters (A–Z), as it will appear on the card.');
      }
      if (!this.els.child.value.trim() || !this.els.age.value || !this.els.date.value) return 'incomplete';
      const age = Number(this.els.age.value);
      if (!(age >= 1 && age <= 18)) return this.t('err_age', 'Please enter an age between 1 and 18.');
      if (this.els.date.value < this.earliest) return this.t('err_soon', "That's too soon for us. The earliest party date we can make is %date%.", {date: uiDate(this.earliest)});
      return '';
    }

    add(e) {
      if (this.problem()) { e.preventDefault(); return; }
      const f = this.els.form;
      f.querySelector('[data-variant-input]').value = this.theme.dataset.variant;
      f.querySelector('[data-qty-input]').value = this.guests();
      f.querySelector('[data-prop-theme]').value = this.theme.dataset.name;
      f.querySelector('[data-prop-child]').value = this.els.child.value.trim();
      f.querySelector('[data-prop-age]').value = String(Math.floor(Number(this.els.age.value)));
      f.querySelector('[data-prop-date]').value = prettyDate(this.els.date.value);

      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return; // normal form post
      e.preventDefault();
      this.els.submit.disabled = true;
      const fd = new FormData(f);
      fd.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
      fd.append('sections_url', window.location.pathname);
      fetch('/cart/add', {method: 'POST', body: fd, headers: {Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest'}})
        .then((r) => r.json())
        .then((res) => {
          if (res.status) { this.els.msg.textContent = res.description || this.t('could_not_add', 'Could not add to cart.'); return; }
          drawer.classList.remove('is-empty');
          drawer.renderContents(res);
        })
        .catch(() => f.submit())
        .finally(() => this.update());
    }
  }

  customElements.define('return-gifts', ReturnGifts);
})();
